// @flow strict

import * as React from 'react';
import cx from 'classnames';
import PageContentBlock from '../PageContentBlock';
import type { BlockRichText } from '../types';
import styles from './styles.scss';
import FormattedContent from '../FormattedContent';
import { Button, Link } from '../Button';
import PardotDialog from '../PardotDialog';

const blockVariantMap = {
  Low: 'Featured',
  Medium: 'Default',
  High: 'DefaultContrast',
  Highest: 'FeaturedContrast',
  Black: 'Dark',
  Silver: 'Silver',
  White: 'White',
  Charcoal: 'Charcoal',
  Citrus: 'Featured',
};

const ctaVariantMap = {
  Low: 'Default',
  Medium: 'Default',
  High: 'Default',
  Highest: 'Primary',
  Black: 'Primary',
  Silver: 'Primary',
  White: 'Primary',
  Charcoal: 'Primary',
  Citrus: 'Underline',
};

type ContentType = {|
  title?: string,
  children: BlockRichText,
  ctaText?: string,
  ctaHref?: string,
  pardotFormUrl?: string,
  pardotFormTitle?: string,
|};

type VariantType =
  | 'Low'
  | 'Medium'
  | 'High'
  | 'Highest'
  | 'Black'
  | 'Silver'
  | 'Charcoal'
  | 'White'
  | 'Citrus';

export type ImagePropTypes = {| image: string, alt?: string |};
export type VideoPropsTypes = {| embeddedId: string |};
type MediaPropType = ImagePropTypes | VideoPropsTypes;

export type TextAndMediaPropTypes = {|
  background: VariantType,
  title?: string,
  intro?: string,
  content: Array<ContentType>,
  position: 'Left' | 'Right',
|};

type PropTypes = {|
  ...TextAndMediaPropTypes,
  // $FlowFixMe
  media: React.ElementType<MediaPropType>,
|};

const Content = (props: { ...ContentType, background: VariantType }) => {
  const [dialogOpen, toggleDialog] = React.useState(false);
  const onClick = () => {
    toggleDialog(true);
  };

  return (
    <div className={styles.content}>
      {props.title && <h2 className={styles.title}>{props.title}</h2>}
      <FormattedContent
        isDarkBackground={props.background === 'Highest' || props.background === 'Black'}
        className={styles.text}
      >
        {props.children}
      </FormattedContent>
      {props.ctaText && props.ctaHref && (
        <Link href={props.ctaHref} variant={ctaVariantMap[props.background]}>
          {props.ctaText}
        </Link>
      )}
      {props.ctaText && props.pardotFormTitle && props.pardotFormUrl && (
        <>
          <Button onClick={onClick} variant={ctaVariantMap[props.background]}>
            {props.ctaText}
          </Button>
          {dialogOpen && (
            <PardotDialog
              formTitle={props.pardotFormTitle}
              formUrl={props.pardotFormUrl}
              onToggle={toggleDialog}
            />
          )}
        </>
      )}
    </div>
  );
};

export default (props: PropTypes) => {
  const classname = cx(styles.textAndMedia, props.position === 'Left' && styles.isLeft);

  return (
    <PageContentBlock
      background={
        Object.prototype.hasOwnProperty.call(blockVariantMap, props.background)
          ? blockVariantMap[props.background]
          : blockVariantMap.Low
      }
      title={props.title}
      intro={props.intro}
    >
      <div className={classname}>
        <div className={styles.mediaContainer}>{props.media}</div>
        <div className={styles.contentContainer}>
          {props.content.map((contentBlock, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Content {...contentBlock} background={props.background} key={index} />
          ))}
        </div>
      </div>
    </PageContentBlock>
  );
};
